/**
 * "Send for training" (plan docs/plans/lms-plan.md 11.2, 8.1, 8.2, 18.25,
 * 18.28; WP-46): the four steps of 11.2 in one dialog, posting POST
 * /admin/sops/:id/versions/:vid/send-for-training (Appendix A) with the body
 * sendForTraining.ts builds.
 *
 *   1 Mode            Read and acknowledge (default) or Quiz; fixed to the course kind once
 *                     the first send has created the SOP's course; the validity, the minutes
 *                     and (when needed) the change summary
 *   2 Retrain policy  8.1's three policies with the catalog editor's own wording (default
 *                     "Everyone retrains now"); version 1 waits for renewal
 *   3 Audience        WP-43's AudienceBuilder and preview (who it reaches, untick names),
 *                     the due window, a message, the force switch; a first send, which has
 *                     no course for POST /admin/campaigns/preview yet, is previewed by the
 *                     send route itself (preview: true; docs/plans/sop-import-plan.md 4.4
 *                     item 6, useFirstSendPreview)
 *   4 Review and send
 *
 * A fresh quiz-mode send only creates the draft (no audience is stored,
 * 18.25): the dialog then links to the course editor, and once that draft is
 * published the tracker offers "Send to people", which opens this dialog with
 * the mode and policy fixed and sends the audience.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, Loader2 } from 'lucide-react';
import Modal from '../../../ui/Modal';
import { fieldCls, primaryBtnCls, secondaryBtnCls } from '../../../ui/styles';
import { useLms } from '../../LmsProvider';
import { emptyAudience, audienceIsEmpty, CAMPAIGN_OUTCOMES } from '../../api/campaigns';
import type { RetrainPolicy } from '../../api/catalog';
import { apiErrorMessage } from '../../api/client';
import {
  SOP_CHANGE_SUMMARY_MAX,
  SOP_DEFAULT_VALIDITY_MONTHS,
  sendSopForTraining,
} from '../../api/sops';
import type { SendSopForTrainingResult, SopDetailSop, SopDetailVersion, SopSendMode } from '../../api/sops';
import AudienceBuilder from '../../components/pickers/AudienceBuilder';
import AudiencePreview, { includedRecipients } from '../../components/pickers/AudiencePreview';
import {
  cardCls,
  checkboxCls,
  ErrorNote,
  formatDay,
  labelCls,
  metaCls,
  OUTCOME_LABELS,
  OutcomePill,
  sectionTitleCls,
} from '../../components/pickers/shared';
import useCampaignPreview from '../../components/pickers/useCampaignPreview';
import { useFirstSendPreview } from './useFirstSendPreview';
import { catalogPaths, POLICY_ORDER, POLICY_TEXT } from '../manage/catalog/model';
import { MODE_HINTS, MODE_LABELS } from './labels';
import {
  buildSendBody,
  DUE_DAYS_MAX,
  DUE_DAYS_MIN,
  modeStepProblems,
  parseWhole,
  policyStepProblems,
  policyTakesDueDays,
  SEND_STEPS,
  sendContextOf,
  sendsCampaign,
} from './sendForTraining';
import type { SendForm, SendKind, SendStep } from './sendForTraining';

export interface SendForTrainingModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called once the server answered; the dialog then shows the outcome until closed. */
  onSent: (result: SendSopForTrainingResult) => void;
  sop: SopDetailSop;
  version: SopDetailVersion;
  kind: SendKind;
}

/** 5.8 default_due_days (the dialog pre-fills it; the Settings sub-view owns the network value). */
const DEFAULT_DUE_DAYS = '30';

function freshForm(lockedMode: SopSendMode | null, isNewCourse: boolean, firstTraining: boolean): SendForm {
  return {
    mode: lockedMode ?? 'read_ack',
    validityMonths: isNewCourse ? String(SOP_DEFAULT_VALIDITY_MONTHS) : '',
    estimatedMinutes: '',
    changeSummary: '',
    // 11.2: "default 'Everyone retrains now' for an SOP change"; version 1 waits for renewal (8.1).
    policy: firstTraining ? 'at_renewal' : 'immediate',
    retrainDueDays: DEFAULT_DUE_DAYS,
    audience: emptyAudience(),
    dueDays: DEFAULT_DUE_DAYS,
    message: '',
    force: false,
    excluded: [],
  };
}

function dueOn(days: number | null): string | null {
  if (days === null) return null;
  return formatDay(new Date(Date.now() + days * 24 * 60 * 60 * 1000).toISOString());
}

function Problems({ items, testId }: { items: string[]; testId: string }) {
  if (items.length === 0) return null;
  return (
    <ul className="list-disc space-y-0.5 pl-5 text-xs text-red-600 dark:text-red-400" data-testid={testId}>
      {items.map((p) => (
        <li key={p}>{p}</li>
      ))}
    </ul>
  );
}

function ChoiceCard({
  name,
  value,
  checked,
  onChange,
  label,
  hint,
  testId,
}: {
  name: string;
  value: string;
  checked: boolean;
  onChange: () => void;
  label: string;
  hint: string;
  testId: string;
}) {
  return (
    <label
      className={`flex min-h-[44px] cursor-pointer items-start gap-3 rounded-xl border p-3 transition-colors duration-150 ${
        checked
          ? 'border-primary-600 bg-primary-50 dark:border-primary-500 dark:bg-primary-900/20'
          : 'border-gray-200 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-700/50'
      }`}
      data-testid={testId}
    >
      <input
        type="radio"
        name={name}
        value={value}
        checked={checked}
        onChange={onChange}
        className="mt-0.5 h-4 w-4 border-gray-300 text-primary-600 focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700"
      />
      <span>
        <span className="block text-sm font-medium text-gray-900 dark:text-gray-100">{label}</span>
        <span className={metaCls}>{hint}</span>
      </span>
    </label>
  );
}

export function SendForTrainingModal({ isOpen, onClose, onSent, sop, version, kind }: SendForTrainingModalProps) {
  const { siteName } = useLms();
  const ctx = useMemo(() => sendContextOf(sop, version, kind), [sop, version, kind]);
  const [step, setStep] = useState<SendStep>(0);
  const [reached, setReached] = useState<SendStep>(0);
  const [form, setForm] = useState<SendForm>(() => freshForm(ctx.lockedMode, ctx.courseId === null, ctx.firstTraining));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<SendSopForTrainingResult | null>(null);

  // A fresh dialog each time it opens; a reload of the SOP behind it (after a send) changes nothing here.
  const wasOpen = useRef(false);
  useEffect(() => {
    if (isOpen && !wasOpen.current) {
      setStep(0);
      setReached(0);
      setForm(freshForm(ctx.lockedMode, ctx.courseId === null, ctx.firstTraining));
      setBusy(false);
      setError(null);
      setResult(null);
    }
    wasOpen.current = isOpen;
  }, [isOpen, ctx]);

  const set = (patch: Partial<SendForm>) => setForm((f) => ({ ...f, ...patch }));
  const mode = ctx.lockedMode ?? form.mode;
  const campaign = sendsCampaign(ctx, mode);

  const asking = isOpen && campaign && !result;
  const campaignPreview = useCampaignPreview({
    courseId: asking ? ctx.courseId : null,
    audience: form.audience,
    courseVersionId: ctx.publishedVersionId,
    force: form.force,
  });
  // A first send has no course yet: the send route previews it (sop-import-plan 4.4 item 6).
  const firstSendPreview = useFirstSendPreview({
    sopId: sop.id,
    versionId: version.id,
    enabled: asking && ctx.courseId === null,
    mode,
    audience: form.audience,
    force: form.force,
  });
  const { preview, loading: previewLoading, error: previewError, reload: reloadPreview } = ctx.courseId ? campaignPreview : firstSendPreview;
  const { people: included } = includedRecipients(preview, form.excluded);

  const dueDays = parseWhole(form.dueDays, DUE_DAYS_MIN, DUE_DAYS_MAX);
  const audienceProblems: string[] = [];
  if (campaign) {
    if (audienceIsEmpty(form.audience)) audienceProblems.push('Choose sites, roles, groups or people.');
    else if (preview && included.length === 0) audienceProblems.push('Nobody is left in the list: tick at least one person.');
    else if (previewError) audienceProblems.push('Fix the audience: the server could not resolve it.');
    if (dueDays === null) audienceProblems.push(`Enter a due window of ${DUE_DAYS_MIN} to ${DUE_DAYS_MAX} whole days.`);
  }
  const problems: Record<SendStep, string[]> = {
    0: modeStepProblems(ctx, { ...form, mode }),
    1: policyStepProblems(ctx, { ...form, mode }),
    2: audienceProblems,
    3: [],
  };
  const canLeave = (s: SendStep) => problems[s].length === 0;

  const goTo = (s: SendStep) => {
    if (s > reached) return;
    setStep(s);
  };
  const next = () => {
    if (!canLeave(step) || step >= 3) return;
    const to = (step + 1) as SendStep;
    setStep(to);
    setReached((r) => (to > r ? to : r));
  };

  const send = async () => {
    if (busy || [0, 1, 2].some((s) => !canLeave(s as SendStep))) return;
    setBusy(true);
    setError(null);
    try {
      const body = buildSendBody(ctx, { ...form, mode }, preview ? preview.recipients.map((r) => r.user_id) : null);
      const out = await sendSopForTraining(sop.id, version.id, body);
      setResult(out);
      onSent(out);
    } catch (err) {
      setError(apiErrorMessage(err, 'The SOP could not be sent for training.'));
    } finally {
      setBusy(false);
    }
  };

  const title = kind === 'send_people'
    ? `Send SOP ${sop.sop_number} version ${version.version_label} to people`
    : `Send SOP ${sop.sop_number} version ${version.version_label} for training`;

  return (
    <Modal isOpen={isOpen} onClose={busy ? () => {} : onClose} title={title} maxWidth="max-w-4xl">
      {result ? (
        <SendOutcome result={result} onClose={onClose} />
      ) : (
        <div className="space-y-4" data-testid="lms-sop-send">
          <ol className="flex flex-wrap items-center gap-2" aria-label="Send for training steps">
            {SEND_STEPS.map((label, i) => {
              const current = i === step;
              const open = i <= reached;
              return (
                <li key={label}>
                  <button
                    type="button"
                    onClick={() => goTo(i as SendStep)}
                    disabled={!open}
                    aria-current={current ? 'step' : undefined}
                    data-testid={`lms-sop-send-step-${i}`}
                    className={`inline-flex min-h-[44px] items-center gap-2 rounded-lg px-2.5 text-sm font-medium transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed sm:min-h-0 sm:py-1.5 ${
                      current
                        ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-400'
                        : open
                          ? 'text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700'
                          : 'text-gray-400 dark:text-gray-500'
                    }`}
                  >
                    <span
                      className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold ${
                        current ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
                      }`}
                    >
                      {i + 1}
                    </span>
                    {label}
                  </button>
                </li>
              );
            })}
          </ol>

          {step === 0 && (
            <section className="space-y-4" aria-label="Mode" data-testid="lms-sop-send-mode">
              {kind === 'send_people' ? (
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  {`${mode === 'quiz' ? 'This SOP is trained with a quiz.' : 'This SOP is trained by reading and acknowledging it.'} Version ${ctx.publishedVersionNo ?? ''} of the training is published and has not been sent to anyone yet.`}
                </p>
              ) : ctx.lockedMode ? (
                <div className={`${cardCls} space-y-1`}>
                  <h3 className={sectionTitleCls}>{MODE_LABELS[ctx.lockedMode]}</h3>
                  <p className={metaCls}>
                    {`${MODE_HINTS[ctx.lockedMode]} The first send of this SOP chose this mode, and a training course keeps its mode.`}
                  </p>
                </div>
              ) : (
                <fieldset className="space-y-2">
                  <legend className={labelCls}>How should people be trained on this SOP?</legend>
                  {(['read_ack', 'quiz'] as SopSendMode[]).map((m) => (
                    <ChoiceCard
                      key={m}
                      name="lms-sop-send-mode"
                      value={m}
                      checked={form.mode === m}
                      onChange={() => set({ mode: m })}
                      label={m === 'read_ack' ? `${MODE_LABELS[m]} (default)` : MODE_LABELS[m]}
                      hint={MODE_HINTS[m]}
                      testId={`lms-sop-mode-${m}`}
                    />
                  ))}
                </fieldset>
              )}

              {kind === 'send' && (
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label htmlFor="lms-sop-validity" className={labelCls}>
                      Valid for (months)
                    </label>
                    <input
                      id="lms-sop-validity"
                      data-testid="lms-sop-validity"
                      type="number"
                      inputMode="numeric"
                      min={1}
                      max={120}
                      className={`${fieldCls} min-h-[44px] w-28 sm:min-h-0`}
                      value={form.validityMonths}
                      placeholder={ctx.courseId ? 'Unchanged' : String(SOP_DEFAULT_VALIDITY_MONTHS)}
                      onChange={(e) => set({ validityMonths: e.target.value })}
                    />
                    <p className={`${metaCls} mt-1`}>
                      {ctx.courseId
                        ? "Leave it empty to keep the training course's validity."
                        : `How long an acknowledgement lasts before it is renewed (default ${SOP_DEFAULT_VALIDITY_MONTHS}).`}
                    </p>
                  </div>
                  <div>
                    <label htmlFor="lms-sop-minutes" className={labelCls}>
                      Estimated minutes <span className="font-normal text-gray-500 dark:text-gray-400">(optional)</span>
                    </label>
                    <input
                      id="lms-sop-minutes"
                      data-testid="lms-sop-minutes"
                      type="number"
                      inputMode="numeric"
                      min={1}
                      max={600}
                      className={`${fieldCls} min-h-[44px] w-28 sm:min-h-0`}
                      value={form.estimatedMinutes}
                      placeholder="5"
                      onChange={(e) => set({ estimatedMinutes: e.target.value })}
                    />
                    <p className={`${metaCls} mt-1`}>The time shown to people; empty uses the standard estimate.</p>
                  </div>
                </div>
              )}

              {kind === 'send' && version.change_summary && (
                <div className="space-y-1">
                  <div className={labelCls}>What changed (from the upload)</div>
                  <p className="whitespace-pre-line text-sm text-gray-700 dark:text-gray-300">{version.change_summary}</p>
                </div>
              )}
              {ctx.needsChangeSummary && (
                <div>
                  <label htmlFor="lms-sop-send-summary" className={labelCls}>
                    What changed (required)
                  </label>
                  <textarea
                    id="lms-sop-send-summary"
                    data-testid="lms-sop-send-summary"
                    className={`${fieldCls} w-full`}
                    rows={3}
                    maxLength={SOP_CHANGE_SUMMARY_MAX}
                    value={form.changeSummary}
                    onChange={(e) => set({ changeSummary: e.target.value })}
                  />
                  <p className={`${metaCls} mt-1`}>This SOP version has no change summary, and people see one first when they retrain.</p>
                </div>
              )}
            </section>
          )}

          {step === 1 && (
            <section className="space-y-3" aria-label="Retrain policy" data-testid="lms-sop-send-policy">
              {kind === 'send_people' ? (
                <p className="text-sm text-gray-700 dark:text-gray-300" data-testid="lms-sop-policy-fixed">
                  {ctx.publishedPolicy
                    ? `The training version was published with "${POLICY_TEXT[ctx.publishedPolicy].label}": current holders were handled then.`
                    : 'The training version is already published: current holders were handled then.'}
                </p>
              ) : mode === 'quiz' ? (
                <p className="text-sm text-gray-700 dark:text-gray-300" data-testid="lms-sop-policy-quiz">
                  You choose what happens to people who already hold this training when you publish the quiz in the course editor.
                </p>
              ) : ctx.firstTraining ? (
                <p className="text-sm text-gray-700 dark:text-gray-300" data-testid="lms-sop-policy-first">
                  This is the first training version of this SOP. Nobody holds it yet, so there is nobody to retrain: it waits for renewal (8.1).
                </p>
              ) : (
                <fieldset className="space-y-2">
                  <legend className={labelCls}>What happens to people who already hold this SOP training</legend>
                  {POLICY_ORDER.map((p: RetrainPolicy) => (
                    <ChoiceCard
                      key={p}
                      name="lms-sop-send-policy"
                      value={p}
                      checked={form.policy === p}
                      onChange={() => set({ policy: p })}
                      label={p === 'immediate' ? `${POLICY_TEXT[p].label} (default for an SOP change)` : POLICY_TEXT[p].label}
                      hint={POLICY_TEXT[p].sentence}
                      testId={`lms-sop-policy-${p}`}
                    />
                  ))}
                  {policyTakesDueDays(form.policy) && (
                    <div>
                      <label htmlFor="lms-sop-retrain-days" className={labelCls}>
                        Retrain within (days)
                      </label>
                      <div className="flex flex-wrap items-center gap-3">
                        <input
                          id="lms-sop-retrain-days"
                          data-testid="lms-sop-retrain-days"
                          type="number"
                          inputMode="numeric"
                          min={DUE_DAYS_MIN}
                          max={DUE_DAYS_MAX}
                          className={`${fieldCls} min-h-[44px] w-28 sm:min-h-0`}
                          value={form.retrainDueDays}
                          onChange={(e) => set({ retrainDueDays: e.target.value })}
                        />
                        {dueOn(parseWhole(form.retrainDueDays, DUE_DAYS_MIN, DUE_DAYS_MAX)) && (
                          <span className={metaCls}>{`Due ${dueOn(parseWhole(form.retrainDueDays, DUE_DAYS_MIN, DUE_DAYS_MAX))}`}</span>
                        )}
                      </div>
                    </div>
                  )}
                </fieldset>
              )}
            </section>
          )}

          {step === 2 && (
            <section className="space-y-4" aria-label="Audience and due date" data-testid="lms-sop-send-audience">
              {!campaign ? (
                <p className="text-sm text-gray-700 dark:text-gray-300" data-testid="lms-sop-audience-later">
                  A quiz is written before anyone takes it. This send creates the quiz draft only; no audience is kept. Once you
                  publish the quiz in the course editor, come back to this SOP and use Send to people.
                </p>
              ) : (
                <>
                  <AudienceBuilder value={form.audience} onChange={(audience) => set({ audience })} siteName={siteName} />
                  <div className={`${cardCls} space-y-2`}>
                    <h3 className={sectionTitleCls}>Who this reaches</h3>
                    {!ctx.courseId && (
                      <p className={metaCls} data-testid="lms-sop-first-preview">
                        This is the first send of this SOP: it creates the training course, so nobody holds it yet.
                      </p>
                    )}
                    <AudiencePreview
                      preview={preview}
                      loading={previewLoading}
                      error={previewError}
                      onRetry={reloadPreview}
                      excluded={form.excluded}
                      onExcludedChange={(excluded) => set({ excluded })}
                    />
                  </div>
                  <div className={`${cardCls} space-y-4`}>
                    <div>
                      <label htmlFor="lms-sop-due-days" className={labelCls}>
                        Due within (days)
                      </label>
                      <div className="flex flex-wrap items-center gap-3">
                        <input
                          id="lms-sop-due-days"
                          data-testid="lms-sop-due-days"
                          type="number"
                          inputMode="numeric"
                          min={DUE_DAYS_MIN}
                          max={DUE_DAYS_MAX}
                          className={`${fieldCls} min-h-[44px] w-28 sm:min-h-0`}
                          value={form.dueDays}
                          onChange={(e) => set({ dueDays: e.target.value })}
                        />
                        {dueOn(dueDays) && <span className={metaCls}>{`Due ${dueOn(dueDays)}`}</span>}
                      </div>
                    </div>
                    <div>
                      <label htmlFor="lms-sop-message" className={labelCls}>
                        Message <span className="font-normal text-gray-500 dark:text-gray-400">(optional; shown in the notice and on the assignment)</span>
                      </label>
                      <textarea
                        id="lms-sop-message"
                        data-testid="lms-sop-message"
                        className={`${fieldCls} w-full`}
                        rows={2}
                        maxLength={2000}
                        value={form.message}
                        onChange={(e) => set({ message: e.target.value })}
                      />
                    </div>
                    <label className="flex min-h-[44px] cursor-pointer items-start gap-3 text-sm sm:min-h-0">
                      <input
                        type="checkbox"
                        className={`${checkboxCls} mt-0.5`}
                        checked={form.force}
                        onChange={(e) => set({ force: e.target.checked })}
                        data-testid="lms-sop-force"
                      />
                      <span>
                        <span className="block font-medium text-gray-900 dark:text-gray-100">Also send to people who already hold a current completion</span>
                        <span className={metaCls}>Without this, people who are already current are left alone.</span>
                      </span>
                    </label>
                  </div>
                </>
              )}
            </section>
          )}

          {step === 3 && (
            <section className={`${cardCls} space-y-2`} aria-label="Review and send" data-testid="lms-sop-send-review">
              <h3 className={sectionTitleCls}>Review</h3>
              <dl className="grid gap-x-4 gap-y-1 text-sm sm:grid-cols-[minmax(0,12rem)_1fr]">
                <dt className="text-gray-500 dark:text-gray-400">SOP</dt>
                <dd className="text-gray-900 dark:text-gray-100">{`${sop.sop_number} ${sop.title}, version ${version.version_label}`}</dd>
                <dt className="text-gray-500 dark:text-gray-400">Mode</dt>
                <dd className="text-gray-900 dark:text-gray-100">{MODE_LABELS[mode]}</dd>
                {kind === 'send' && mode === 'read_ack' && (
                  <>
                    <dt className="text-gray-500 dark:text-gray-400">Current holders</dt>
                    <dd className="text-gray-900 dark:text-gray-100">
                      {ctx.firstTraining
                        ? POLICY_TEXT.at_renewal.label
                        : `${POLICY_TEXT[form.policy ?? 'immediate'].label}${policyTakesDueDays(form.policy) ? `, within ${form.retrainDueDays} days` : ''}`}
                    </dd>
                  </>
                )}
                {campaign ? (
                  <>
                    <dt className="text-gray-500 dark:text-gray-400">People</dt>
                    <dd className="text-gray-900 dark:text-gray-100" data-testid="lms-sop-review-people">
                      {preview
                        ? `${included.length.toLocaleString()} ${included.length === 1 ? 'person' : 'people'}`
                        : 'Resolved when you send'}
                    </dd>
                    <dt className="text-gray-500 dark:text-gray-400">Due</dt>
                    <dd className="text-gray-900 dark:text-gray-100">{dueOn(dueDays) ?? ''}</dd>
                    {form.force && (
                      <>
                        <dt className="text-gray-500 dark:text-gray-400">Force</dt>
                        <dd className="text-gray-900 dark:text-gray-100">Also people who are already current</dd>
                      </>
                    )}
                  </>
                ) : (
                  <>
                    <dt className="text-gray-500 dark:text-gray-400">Next</dt>
                    <dd className="text-gray-900 dark:text-gray-100">The quiz draft opens in the course editor for its questions.</dd>
                  </>
                )}
              </dl>
            </section>
          )}

          <Problems items={problems[step]} testId={`lms-sop-send-problems-${step}`} />
          {error && <ErrorNote testId="lms-sop-send-error">{error}</ErrorNote>}

          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 pt-4 dark:border-gray-700">
            <button type="button" className={secondaryBtnCls} onClick={onClose} disabled={busy}>
              Cancel
            </button>
            <div className="flex items-center gap-2">
              {step > 0 && (
                <button type="button" className={secondaryBtnCls} onClick={() => setStep((step - 1) as SendStep)} disabled={busy} data-testid="lms-sop-send-back">
                  Back
                </button>
              )}
              {step < 3 ? (
                <button type="button" className={primaryBtnCls} onClick={next} disabled={!canLeave(step)} data-testid="lms-sop-send-next">
                  Next
                </button>
              ) : (
                <button type="button" className={primaryBtnCls} onClick={send} disabled={busy} data-testid="lms-sop-send-submit">
                  {busy && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
                  {campaign ? 'Send for training' : 'Create the quiz draft'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}

/** What the send did (8.2 step 4's outcome counts; 11.2's quiz draft). */
function SendOutcome({ result, onClose }: { result: SendSopForTrainingResult; onClose: () => void }) {
  const outcomes = result.outcomes;
  return (
    <div className="space-y-4" data-testid="lms-sop-send-result">
      {result.campaign ? (
        <>
          <p className="flex items-start gap-2 text-sm text-gray-900 dark:text-gray-100">
            <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-green-600 dark:text-green-400" aria-hidden="true" />
            {`Sent for training: version ${result.version.version_no} of ${result.course.title}.`}
          </p>
          {outcomes && (
            <ul className="flex flex-wrap gap-2" data-testid="lms-sop-send-outcomes">
              {CAMPAIGN_OUTCOMES.map((o) => (
                <li key={o} className="flex items-center gap-1.5 text-sm">
                  <OutcomePill outcome={o} />
                  <span className="tabular-nums" aria-label={OUTCOME_LABELS[o]}>
                    {(outcomes[o] ?? 0).toLocaleString()}
                  </span>
                </li>
              ))}
            </ul>
          )}
          {result.publish && result.publish.superseded_completions > 0 && (
            <p className={metaCls}>{`${result.publish.superseded_completions.toLocaleString()} earlier completions were superseded by the publish.`}</p>
          )}
        </>
      ) : (
        <div className="space-y-2" data-testid="lms-sop-send-draft">
          <p className="flex items-start gap-2 text-sm text-gray-900 dark:text-gray-100">
            <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-green-600 dark:text-green-400" aria-hidden="true" />
            {`The quiz draft (version ${result.version.version_no}) is ready. Write its questions and publish it in the course editor, then send it to people from this SOP.`}
          </p>
          <Link to={catalogPaths.version(result.version.id)} className={primaryBtnCls} data-testid="lms-sop-edit-quiz">
            Open the quiz draft
          </Link>
        </div>
      )}
      <div className="flex justify-end border-t border-gray-100 pt-4 dark:border-gray-700">
        <button type="button" className={secondaryBtnCls} onClick={onClose}>
          Done
        </button>
      </div>
    </div>
  );
}

export default SendForTrainingModal;
